import { formatDate } from '../utils/formatDate';
import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  Plus,
  Loader,
  AlertCircle,
  Users,
  ArrowRight,
} from 'lucide-react';
import { clientsService, facilitiesService } from '../services';
import AddClientModal from '../components/Forms/AddClientModal';
import type { Client, Facility } from '../types';
import './ClientDirectory.css';

interface ClientSummary extends Client {
  facilityCount: number;
  facilities: Facility[];
}

const ClientDirectory: React.FC = () => {
  usePageTitle('Clients');
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientsData, facilitiesData] = await Promise.all([
        clientsService.getClients(),
        facilitiesService.getFacilities(),
      ]);

      const enriched: ClientSummary[] = clientsData.map((client: Client) => {
        const clientFacilities = facilitiesData.filter(
          (f: Facility) => f.client_id === client.id
        );
        return {
          ...client,
          facilityCount: clientFacilities.length,
          facilities: clientFacilities,
        };
      });

      setClients(enriched);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClientClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  if (loading) {
    return (
      <div className="client-directory">
        <div className="client-directory__loading">
          <Loader className="loading-spinner" size={32} />
          <p>Loading clients...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-directory">
        <div className="client-directory__error">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-tour="clients" className="client-directory">
      <div className="client-directory__header">
        <div className="client-directory__header-text">
          <h1>Clients</h1>
          <p>Browse and manage all client accounts</p>
        </div>
        <button
          data-tour="add-client"
          className="btn btn-primary"
          onClick={() => setShowAddClientModal(true)}
        >
          <Plus size={18} />
          Add Client
        </button>
      </div>

      <div className="client-directory__toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="client-directory__count">
          {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="client-directory__empty">
          <Users size={48} />
          <h3>{searchTerm ? 'No clients match your search' : 'No clients yet'}</h3>
          <p>
            {searchTerm
              ? 'Try a different search term.'
              : 'Add a client to get started.'}
          </p>
          {!searchTerm && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddClientModal(true)}
            >
              <Plus size={18} />
              Add Client
            </button>
          )}
        </div>
      ) : (
        <div className="client-directory__grid">
          {filteredClients.map((client) => (
            <button
              key={client.id}
              type="button"
              className="client-directory__card card"
              onClick={() => handleClientClick(client.id)}
            >
              <div className="client-card__avatar">
                <Building2 size={28} />
              </div>
              <div className="client-card__body">
                <h2 className="client-card__name">{client.name}</h2>
                <div className="client-card__meta">
                  <span className="client-card__stat">
                    <Building2 size={14} />
                    {client.facilityCount} facilit{client.facilityCount !== 1 ? 'ies' : 'y'}
                  </span>
                  {client.facilities.length > 0 && (
                    <span className="client-card__address">
                      {client.facilities[0].address}
                    </span>
                  )}
                </div>
                <div className="client-card__since">
                  Client since {formatDate(client.created_at)}
                </div>
              </div>
              <div className="client-card__arrow">
                <ArrowRight size={20} />
              </div>
            </button>
          ))}
        </div>
      )}

      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onSuccess={() => {
          loadData();
          setShowAddClientModal(false);
        }}
      />
    </div>
  );
};

export default ClientDirectory;
